import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from './api';

interface Person { id: number; company_id: number; display_name: string; default_department_id: number | null }
interface Project { id: number; company_id: number; code: string; name: string }
interface Department { id: number; company_id: number; code: string; name: string; sector_id: number | null; kanal: string | null }
interface Sector { id: number; company_id: number; code: string; name_hr: string; name_en: string }
interface Bootstrap {
    companies: { id: number; code: string; name: string }[];
    persons: Person[];
    projects: Project[];
    departments: Department[];
    sectors: Sector[];
}
interface Ent { id: number; code?: string | null; name?: string | null; name_hr?: string | null; display_name?: string | null; kanal?: string | null }
export interface OrderThread {
    id: number | null; public_code: string | null;
    company: Ent | null; sector: Ent | null; department: Ent | null;
    cost_place_node: Ent | null; responsible_person: Ent | null; project: Ent | null;
    canonical_text: string | null;
}

// Bootstrap se dohvaća jednom po sesiji (codebook je stabilan unutar drawer-otvaranja).
let bootstrapCache: Promise<Bootstrap> | null = null;
function loadBootstrap(token: string | null): Promise<Bootstrap> {
    if (! bootstrapCache) {
        bootstrapCache = api<Bootstrap>('/admin/purchase-order-codes/bootstrap', token);
    }
    return bootstrapCache;
}

const entLabel = (e: Ent | null): string => e ? (e.display_name || e.name_hr || e.name || e.code || `#${e.id}`) : '—';

/**
 * Dvosmjerni unos šifre narudžbe (m.125):
 *  - upišeš javnu šifru → na blur se razveže (resolve) i ispiše detalje + napuni dropdowne;
 *  - odabereš osobu+projekt+(sektor/kanal/poslovnica) → "Generiraj" sastavi šifru (from-minimal).
 *  Sektor i Kanal su atributi poslovnice (odjela) i filtriraju listu poslovnica.
 */
export function OrderCodeField({ token, locale, companyId, value, onChange }: {
    token: string | null;
    locale: 'hr' | 'en';
    companyId: string | number;
    value: string;
    onChange: (code: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [boot, setBoot] = useState<Bootstrap | null>(null);
    const [personId, setPersonId] = useState('');
    const [projectId, setProjectId] = useState('');
    const [sectorId, setSectorId] = useState('');
    const [kanalVal, setKanalVal] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [thread, setThread] = useState<OrderThread | null>(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => { void loadBootstrap(token).then(setBoot).catch(() => setMsg(t('Greška učitavanja šifrarnika', 'Codebook load error'))); }, [token]);

    const cid = Number(companyId);
    const persons = (boot?.persons ?? []).filter((p) => Number(p.company_id) === cid);
    const projects = (boot?.projects ?? []).filter((p) => Number(p.company_id) === cid);
    const sectors = (boot?.sectors ?? []).filter((s) => Number(s.company_id) === cid);
    const kanali = Array.from(new Set((boot?.departments ?? []).filter((d) => Number(d.company_id) === cid).map((d) => d.kanal).filter((k): k is string => !! k)));
    const departments = (boot?.departments ?? []).filter((d) => Number(d.company_id) === cid
        && (! sectorId || String(d.sector_id ?? '') === sectorId)
        && (! kanalVal || d.kanal === kanalVal));

    const resolveCode = useCallback(async (code: string) => {
        const c = code.trim();
        if (c === '') { setThread(null); setMsg(''); return; }
        try {
            const q = new URLSearchParams({ public_code: c });
            if (cid) q.set('company_id', String(cid));
            const res = await api<{ thread: OrderThread }>(`/admin/purchase-order-codes/resolve?${q.toString()}`, token);
            setThread(res.thread);
            if (res.thread?.responsible_person) setPersonId(String(res.thread.responsible_person.id));
            if (res.thread?.project) setProjectId(String(res.thread.project.id));
            if (res.thread?.sector) setSectorId(String(res.thread.sector.id));
            if (res.thread?.department?.kanal) setKanalVal(res.thread.department.kanal);
            if (res.thread?.department) setDepartmentId(String(res.thread.department.id));
            setMsg('');
        } catch {
            setThread(null);
            setMsg(t('Šifra nije pronađena', 'Code not found'));
        }
    }, [token, cid, locale]);

    // Razveži postojeću šifru pri otvaranju (kad obveza već ima šifru).
    useEffect(() => { if (value && value.trim() !== '') void resolveCode(value); /* eslint-disable-next-line */ }, [boot]);

    // Promjena tvrtke → očisti odabire koji više ne pripadaju.
    useEffect(() => {
        if (personId && ! persons.some((p) => String(p.id) === personId)) setPersonId('');
        if (projectId && ! projects.some((p) => String(p.id) === projectId)) setProjectId('');
        if (sectorId && ! sectors.some((s) => String(s.id) === sectorId)) setSectorId('');
        if (kanalVal && ! kanali.includes(kanalVal)) setKanalVal('');
        if (departmentId && ! (boot?.departments ?? []).some((d) => String(d.id) === departmentId && Number(d.company_id) === cid)) setDepartmentId('');
        /* eslint-disable-next-line */
    }, [cid, boot]);

    function pickDepartment(v: string) {
        setDepartmentId(v);
        const d = (boot?.departments ?? []).find((x) => String(x.id) === v);
        if (d) {
            if (d.sector_id != null) setSectorId(String(d.sector_id));
            if (d.kanal) setKanalVal(d.kanal);
        }
    }

    async function generate() {
        if (! personId || ! projectId) { setMsg(t('Odaberi osobu i projekt.', 'Select person and project.')); return; }
        setBusy(true); setMsg('');
        try {
            const body: Record<string, unknown> = { responsible_user_id: Number(personId), project_id: Number(projectId) };
            if (departmentId) body.department_id = Number(departmentId);
            const res = await mutate<{ public_code: string; thread: OrderThread }>('/admin/purchase-order-codes/from-minimal', token, 'POST', body);
            onChange(res.public_code);
            setThread(res.thread);
            setMsg(t('✓ Šifra generirana', '✓ Code generated'));
        } catch (err) {
            setMsg(err instanceof Error ? err.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    return (
        <div style={{ gridColumn: '1 / -1', border: '1px solid #e6dcc4', borderRadius: 10, padding: '10px 12px', background: '#fffdf6' }}>
            <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--ad-ink)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                {t('Šifra narudžbe', 'Order code')}
            </div>

            {/* 1) upiši šifru → razveži */}
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={(e) => void resolveCode(e.target.value)}
                placeholder={t('Upiši javnu šifru pa izađi iz polja → detalji', 'Type public code, blur → details')}
                spellCheck={false}
                autoComplete="off"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.9rem' }}
            />

            {/* 2) sastavi iz dropdownova → generiraj */}
            <div style={{ fontSize: '0.74rem', color: '#8a7a55', margin: '10px 0 4px' }}>{t('… ili sastavi i generiraj:', '… or compose and generate:')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <select value={personId} onChange={(e) => setPersonId(e.target.value)} style={sel}>
                    <option value="">{t('Osoba zaduženja…', 'Responsible person…')}</option>
                    {persons.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                </select>
                <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={sel}>
                    <option value="">{t('Projekt…', 'Project…')}</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
                </select>
                <select
                    value={sectorId}
                    onChange={(e) => {
                        const v = e.target.value;
                        setSectorId(v);
                        if (v && departmentId) {
                            const d = (boot?.departments ?? []).find((x) => String(x.id) === departmentId);
                            if (d && String(d.sector_id ?? '') !== v) setDepartmentId('');
                        }
                    }}
                    style={sel}
                >
                    <option value="">{t('Sektor…', 'Sector…')}</option>
                    {sectors.map((s) => <option key={s.id} value={s.id}>{locale === 'en' ? s.name_en : s.name_hr}</option>)}
                </select>
                <select
                    value={kanalVal}
                    onChange={(e) => {
                        const v = e.target.value;
                        setKanalVal(v);
                        if (v && departmentId) {
                            const d = (boot?.departments ?? []).find((x) => String(x.id) === departmentId);
                            if (d && d.kanal !== v) setDepartmentId('');
                        }
                    }}
                    style={sel}
                >
                    <option value="">{t('Kanal…', 'Channel…')}</option>
                    {kanali.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <select value={departmentId} onChange={(e) => pickDepartment(e.target.value)} style={sel}>
                    <option value="">{t('Poslovnica (opc.)', 'Department (opt.)')}</option>
                    {departments.map((d) => <option key={d.id} value={d.id}>{d.code} · {d.name}</option>)}
                </select>
                <button type="button" disabled={busy || ! personId || ! projectId} onClick={() => void generate()}
                    style={{ padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--ad-acc)', color: '#fff', fontWeight: 600, fontSize: '0.82rem', opacity: (! personId || ! projectId) ? 0.5 : 1 }}>
                    {busy ? '…' : t('Generiraj', 'Generate')}
                </button>
            </div>

            {/* detalji (razvezano / sastavljeno) */}
            {thread ? (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: '0.8rem', color: '#3a2f17' }}>
                    {([['Tvrtka', entLabel(thread.company)], ['Sektor', entLabel(thread.sector)], ['Kanal', thread.department?.kanal ?? null], ['Poslovnica', entLabel(thread.department)], ['MT', entLabel(thread.cost_place_node)], ['Osoba', entLabel(thread.responsible_person)], ['Projekt', entLabel(thread.project)]] as [string, string | null][])
                        .filter(([, v]) => v && v !== '—')
                        .map(([lbl, v]) => (
                            <span key={lbl}><span style={{ color: '#8a7a55' }}>{lbl}:</span> <strong>{v}</strong></span>
                        ))}
                </div>
            ) : null}
            {msg ? <div style={{ marginTop: 8, fontSize: '0.78rem', color: msg.startsWith('✓') ? '#1f7d4d' : '#b3261e' }}>{msg}</div> : null}
        </div>
    );
}

const sel: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.84rem', background: '#fff', minWidth: 140 };
